import React, { FC, useState, useMemo } from 'react';
import { AnyFCO, EntityFCO, EntityFCOFields, FCOFields, FCOType } from '../../../core/types/fcoTypes';

import {
  ComboFilterCategory,
  ComboFilterDateSelector,
  ComboFilterHierarchicalList,
  ComboFilterHierarchyState,
  ComboFilterListSelector,
  ComboFilterRangeSelector,
  EmptyProps,
  ExtendedTableColumn,
  Link,
  Select,
  Table,
  TableViewLayout,
  useTableFilters,
} from '@lithos';

import {
  isInBounds,
  isInDateRange,
  getFilteredItems,
  tableStringSortParameters,
  tableArrayLengthSortParameters,
  tableDateSortParameters,
} from '@lithos/TableUtils';

import { isEqual, startCase, uniq } from 'lodash';

import RelatedFCOsCell from '../fcoListView/RelatedFCOsCell';
import TagsCell from '../fcoListView/TagsCell';
import DateCell from '../fcoListView/DateCell';
import { areAnyCategoryMembersCurrentlyActive, countHierarchyActiveSelections } from '@lithos/ComboFilterUtils';
import { defaultDateState, defaultRangeState, defaultTagsState } from '../fcoListView/utils';
import JoinKeysCell from '../fcoListView/JoinKeysCell';
import TableColumnControls from '../fcoListView/TableColumnControls';
import TableSelectors from '../fcoListView/TableSelectors';

interface EntitiesListProps {
  items: EntityFCO[];
  idToFcoMap: Record<string, AnyFCO>;
  workspace: string;
}

enum EntitiesListColumnKeys {
  NAME,
  TAGS,
  JOIN_KEYS,
  NUMBER_OF_FEATURE_VIEWS,
  FEATURE_VIEWS,
  NUMBER_OF_FEATURE_SERVICES,
  FEATURE_SERVICES,
  CREATED_BY,
  CREATED_AT,
  LAST_UPDATED_BY,
  LAST_UPDATED_AT,
}

interface EntitiesListFilterState {
  tags: ComboFilterHierarchyState;
  'Join Keys': string[];
  '# of Feature Views': [number, number];
  'Feature Views': string[];
  '# of Feature Services': [number, number];
  'Feature Services': string[];
  createdBy: string[];
  createdAt: [Date, Date];
  lastUpdatedBy: string[];
  lastUpdatedAt: [Date, Date];
}

const EntitiesList: FC<EntitiesListProps> = ({ items, idToFcoMap, workspace }) => {
  // Always need to set a default filter state
  const defaultFilterState: EntitiesListFilterState = useMemo(
    (): EntitiesListFilterState => ({
      tags: defaultTagsState(items),
      'Join Keys': [],
      '# of Feature Views': defaultRangeState(items, EntityFCOFields.DEPENDENT_FEATURE_VIEWS),
      'Feature Views': [],
      '# of Feature Services': defaultRangeState(items, EntityFCOFields.DEPENDENT_FEATURE_SERVICES),
      'Feature Services': [],
      createdBy: [],
      createdAt: defaultDateState(items, FCOFields.CREATED_DATE),
      lastUpdatedBy: [],
      lastUpdatedAt: defaultDateState(items, FCOFields.LAST_MODIFIED_DATE),
    }),
    [items]
  );

  const allValidOptions = useMemo(() => {
    return {
      Tags: Array.from(Object.keys(defaultTagsState(items))),
      'Join Keys': uniq(items.map((item) => item[EntityFCOFields.JOIN_KEYS])).flat(),
      'Feature Views': uniq(items.map((item) => item[EntityFCOFields.DEPENDENT_FEATURE_VIEWS]))
        .flat()
        .filter((entity) => entity !== undefined) as string[],
      'Feature Services': uniq(items.map((item) => item[EntityFCOFields.DEPENDENT_FEATURE_SERVICES]))
        .flat()
        .filter((entity) => entity !== undefined) as string[],
      Owner: uniq(items.map((item) => item[FCOFields.OWNER] ?? '')).filter(
        (entity) => entity !== undefined
      ) as string[],
      'Last Updated By': uniq(items.map((item) => item[FCOFields.LAST_MODIFIED_BY] ?? '')).filter(
        (entity) => entity !== undefined
      ) as string[],
    };
  }, [items]);

  // Shared logic and states for filtered table views
  const {
    searchQuery,
    hasAnyFilters,
    activeFilterCount,
    sortType,
    setSortType,
    activeHeading,
    setActiveHeading,
    filterState,
    onSearchInputChange,
    handleResetFilters,
    handleFilterChange,
  } = useTableFilters<EntityFCO, EntitiesListColumnKeys, EntitiesListFilterState>(defaultFilterState);

  // Filter components are the actual input components (e.g. A combo filter listt)
  // These are defined separately from the ComboFilters because they are reused in the table header
  // They may also be reused in shortcut popovers in the general filter list
  // This allows us to reference the same component in multiple places
  const filterComponents: Record<
    EntitiesListColumnKeys,
    {
      filter: React.ReactNode;
      activeCount: () => number;
      filterCallback: (entity: EntityFCO) => boolean;
      name: string;
    }
  > = useMemo(() => {
    return {
      [EntitiesListColumnKeys.NAME]: {
        filter: <></>,
        activeCount: () => 0,
        filterCallback: () => true,
        name: 'Name',
      },
      [EntitiesListColumnKeys.TAGS]: {
        filter: (
          <ComboFilterHierarchicalList
            items={filterState.tags}
            setItems={(tags) => {
              handleFilterChange(filterComponents, 'tags', tags);
            }}
          />
        ),
        activeCount: () => countHierarchyActiveSelections(filterState.tags),
        filterCallback: (entity) => {
          let hasEachCategoryMatched = true;
          const categories = Object.keys(filterState.tags);
          categories.forEach((category) => {
            if (areAnyCategoryMembersCurrentlyActive(filterState.tags[category])) {
              const activeSubcategories = Object.entries(filterState.tags[category])
                .filter((entry) => entry[1])
                .map((result) => result[0]);
              const hasMatch = entity.tags.find(
                (tag) => tag.key === category && activeSubcategories.includes(tag.value)
              );
              if (hasMatch === undefined) {
                hasEachCategoryMatched = false;
              }
            }
          });

          return hasEachCategoryMatched;
        },
        name: 'Tags',
      },
      [EntitiesListColumnKeys.JOIN_KEYS]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Join Keys']}
            setSelected={(type) => handleFilterChange(filterComponents, 'Join Keys', type)}
            icon={'Entities'}
            items={uniq(items.map((item) => item[EntityFCOFields.JOIN_KEYS]).flat()).sort()}
            title={''}
          />
        ),
        activeCount: () => filterState['Join Keys'].length,
        filterCallback: (entity) => {
          return entity[EntityFCOFields.JOIN_KEYS].some((key) => filterState['Join Keys'].includes(key));
        },
        name: 'Join Keys',
      },
      [EntitiesListColumnKeys.NUMBER_OF_FEATURE_VIEWS]: {
        filter: (
          <ComboFilterRangeSelector
            value={filterState['# of Feature Views']}
            setValue={(range) => {
              handleFilterChange(filterComponents, '# of Feature Views', range);
            }}
            bounds={defaultFilterState['# of Feature Views']}
          />
        ),
        activeCount: () =>
          isEqual(filterState['# of Feature Views'], defaultFilterState['# of Feature Views']) ? 0 : 1,
        filterCallback: (entity) => {
          return isInBounds(entity[EntityFCOFields.DEPENDENT_FEATURE_VIEWS].length, filterState['# of Feature Views']);
        },
        name: '# of Feature Views',
      },
      [EntitiesListColumnKeys.FEATURE_VIEWS]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Feature Views']}
            setSelected={(type) => handleFilterChange(filterComponents, 'Feature Views', type)}
            icon={'FeatureView'}
            items={uniq(
              items
                .map((item) => item[EntityFCOFields.DEPENDENT_FEATURE_VIEWS])
                .flat()
                .filter((entity) => entity !== undefined)
                .map((id) => idToFcoMap[id][FCOFields.NAME])
            )
              .sort()
              .map(startCase)}
            title={''}
          />
        ),
        activeCount: () => filterState['Feature Views'].length,
        filterCallback: (entity) => {
          const featureViewNames = entity[EntityFCOFields.DEPENDENT_FEATURE_VIEWS].map(
            (featureViewId) => idToFcoMap[featureViewId]
          );
          return (
            filterState['Feature Views'].length === 0 ||
            featureViewNames.some((fv) => filterState['Feature Views'].includes(startCase(fv[FCOFields.NAME])))
          );
        },
        name: 'Feature Views',
      },
      [EntitiesListColumnKeys.NUMBER_OF_FEATURE_SERVICES]: {
        filter: (
          <ComboFilterRangeSelector
            value={filterState['# of Feature Services']}
            setValue={(range) => {
              handleFilterChange(filterComponents, '# of Feature Services', range);
            }}
            bounds={defaultFilterState['# of Feature Services']}
          />
        ),
        activeCount: () =>
          isEqual(filterState['# of Feature Services'], defaultFilterState['# of Feature Services']) ? 0 : 1,
        filterCallback: (entity) => {
          return isInBounds(
            entity[EntityFCOFields.DEPENDENT_FEATURE_SERVICES].length,
            filterState['# of Feature Services']
          );
        },
        name: '# of Feature Services',
      },
      [EntitiesListColumnKeys.FEATURE_SERVICES]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['Feature Services']}
            setSelected={(type) => handleFilterChange(filterComponents, 'Feature Services', type)}
            icon="FeatureService"
            items={uniq(
              items
                .map((item) => item[EntityFCOFields.DEPENDENT_FEATURE_SERVICES])
                .flat()
                .filter((entity) => entity !== undefined)
                .map((id) => idToFcoMap[id][FCOFields.NAME])
            )
              .sort()
              .map(startCase)}
            title={''}
          />
        ),
        activeCount: () => filterState['Feature Services'].length,
        filterCallback: (entity) => {
          const featureServiceNames = entity[EntityFCOFields.DEPENDENT_FEATURE_SERVICES]
            .map((featureServiceId) => idToFcoMap[featureServiceId])
            .map((featureService) => featureService.name);

          return (
            filterState['Feature Services'].length === 0 ||
            featureServiceNames.some((featureService) =>
              filterState['Feature Services'].includes(startCase(featureService))
            )
          );
        },
        name: 'Feature Services',
      },
      [EntitiesListColumnKeys.LAST_UPDATED_BY]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.lastUpdatedBy}
            setSelected={(type) => handleFilterChange(filterComponents, 'lastUpdatedBy', type)}
            items={uniq(
              items.map((item) => item[FCOFields.LAST_MODIFIED_BY] ?? '').filter((entity) => entity !== undefined)
            ).sort()}
            title={''}
          />
        ),
        activeCount: () => filterState.lastUpdatedBy.length,
        filterCallback: (entity) => {
          return filterState.lastUpdatedBy.includes(entity[FCOFields.LAST_MODIFIED_BY] ?? '');
        },
        name: 'Last Updated By',
      },
      [EntitiesListColumnKeys.LAST_UPDATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            start={filterState.lastUpdatedAt[0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'lastUpdatedAt', [date as Date, filterState.lastUpdatedAt[1]]);
            }}
            end={filterState.lastUpdatedAt[1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'lastUpdatedAt', [filterState.lastUpdatedAt[0], date as Date]);
            }}
          />
        ),
        activeCount: () => (isEqual(filterState.lastUpdatedAt, defaultFilterState.lastUpdatedAt) ? 0 : 1),
        filterCallback: (entity) => {
          return isInDateRange(entity[FCOFields.LAST_MODIFIED_DATE], filterState.lastUpdatedAt);
        },
        name: 'Last Updated At',
      },
      [EntitiesListColumnKeys.CREATED_BY]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState.createdBy}
            setSelected={(type) => handleFilterChange(filterComponents, 'createdBy', type)}
            items={uniq(
              items.map((item) => item[FCOFields.OWNER] ?? '').filter((entity) => entity !== undefined)
            ).sort()}
            title={''}
          />
        ),
        activeCount: () => filterState.createdBy.length,
        filterCallback: (entity) => {
          return filterState.createdBy.includes(entity[FCOFields.OWNER] ?? '');
        },
        name: 'Owner',
      },
      [EntitiesListColumnKeys.CREATED_AT]: {
        filter: (
          <ComboFilterDateSelector
            start={filterState.createdAt[0]}
            setStart={(date) => {
              handleFilterChange(filterComponents, 'createdAt', [date as Date, filterState.createdAt[1]]);
            }}
            end={filterState.lastUpdatedAt[1]}
            setEnd={(date) => {
              handleFilterChange(filterComponents, 'createdAt', [filterState.createdAt[0], date as Date]);
            }}
          />
        ),
        activeCount: () => (isEqual(filterState.createdAt, defaultFilterState.createdAt) ? 0 : 1),
        filterCallback: (entity) => {
          return isInDateRange(entity[FCOFields.CREATED_DATE], filterState.createdAt);
        },
        name: 'Created At',
      },
    };
  }, [filterState, items, idToFcoMap, defaultFilterState, handleFilterChange]);

  // These define the filter categories in the Filter button popover
  const categories: ComboFilterCategory[] = [
    {
      name: 'Tags',
      icon: 'Tag',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'tags', defaultFilterState.tags);
      },
      activeCount: countHierarchyActiveSelections(filterState.tags),
      popover: filterComponents[EntitiesListColumnKeys.TAGS].filter,
      disabled: allValidOptions['Tags'].length === 0,
    },
    {
      name: 'Join Keys',
      icon: 'Entities',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Join Keys', defaultFilterState['Join Keys']);
      },
      activeCount: filterComponents[EntitiesListColumnKeys.JOIN_KEYS].activeCount(),
      popover: filterComponents[EntitiesListColumnKeys.JOIN_KEYS].filter,
      disabled: allValidOptions['Join Keys'].length === 0,
    },
    {
      name: '# of Feature Views',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Feature Views', defaultFilterState['# of Feature Views']);
      },
      activeCount: isEqual(filterState['# of Feature Views'], defaultFilterState['# of Feature Views']) ? 0 : 1,
      popover: filterComponents[EntitiesListColumnKeys.NUMBER_OF_FEATURE_VIEWS].filter,
    },
    {
      name: 'Feature Views',
      icon: 'FeatureView',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Feature Views', defaultFilterState['Feature Views']);
      },
      activeCount: filterState['Feature Views'].length,
      popover: filterComponents[EntitiesListColumnKeys.FEATURE_VIEWS].filter,
      disabled: allValidOptions['Feature Views'].length === 0,
    },
    {
      name: '# of Feature Services',
      icon: 'FeatureService',
      resetCallback: () => {
        handleFilterChange(filterComponents, '# of Feature Services', defaultFilterState['# of Feature Services']);
      },
      activeCount: isEqual(filterState['# of Feature Services'], defaultFilterState['# of Feature Services']) ? 0 : 1,
      popover: filterComponents[EntitiesListColumnKeys.NUMBER_OF_FEATURE_SERVICES].filter,
    },
    {
      name: 'Feature Services',
      icon: 'FeatureService',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'Feature Services', defaultFilterState['Feature Services']);
      },
      activeCount: filterState['Feature Services'].length,
      popover: filterComponents[EntitiesListColumnKeys.FEATURE_SERVICES].filter,
      disabled: allValidOptions['Feature Services'].length === 0,
    },
    {
      name: 'Owner',
      icon: 'UserCircle',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'createdBy', defaultFilterState.createdBy);
      },
      activeCount: filterState.createdBy.length,
      popover: filterComponents[EntitiesListColumnKeys.CREATED_BY].filter,
      disabled: allValidOptions['Owner'].length === 0,
    },
    {
      name: 'Created At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'createdAt', defaultFilterState.createdAt);
      },
      activeCount: isEqual(filterState.createdAt, defaultFilterState.createdAt) ? 0 : 1,
      popover: filterComponents[EntitiesListColumnKeys.CREATED_AT].filter,
    },
    {
      name: 'Last Updated By',
      icon: 'UserCircle',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'lastUpdatedBy', defaultFilterState.lastUpdatedBy);
      },
      activeCount: filterState.lastUpdatedBy.length,
      popover: filterComponents[EntitiesListColumnKeys.LAST_UPDATED_BY].filter,
      disabled: allValidOptions['Last Updated By'].length === 0,
    },
    {
      name: 'Last Updated At',
      icon: 'Calendar',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'lastUpdatedAt', defaultFilterState.lastUpdatedAt);
      },
      activeCount: isEqual(filterState.lastUpdatedAt, defaultFilterState.lastUpdatedAt) ? 0 : 1,
      popover: filterComponents[EntitiesListColumnKeys.LAST_UPDATED_AT].filter,
    },
  ];

  // Define the columns for the table
  const columns: ExtendedTableColumn<EntityFCO>[] = [
    {
      name: 'Name',
      id: EntitiesListColumnKeys.NAME,
      content: (entity) => (
        <Link
          name={entity[FCOFields.NAME]}
          icon="Sources"
          url={`/repo/${workspace}/entities/${entity[FCOFields.NAME]}/overview`}
          testId={`entities-link-${entity[FCOFields.NAME]}`}
        />
      ),
      columnWidth: 'l',
      ...tableStringSortParameters(sortType, FCOFields.NAME, setSortType),
    },
    {
      name: 'Tags',
      id: EntitiesListColumnKeys.TAGS,
      content: (entity) => <TagsCell fco={entity} />,
      filterContent: filterComponents[EntitiesListColumnKeys.TAGS].filter,
      ...tableArrayLengthSortParameters(sortType, FCOFields.TAGS, setSortType),
    },
    {
      // Sorting by Join Keys may be confusing because Join Keys are technically an array, so we sort by the count
      // However, entities often have a single join key, so it might look like nothing is sorting
      name: 'Join Keys',
      id: EntitiesListColumnKeys.JOIN_KEYS,
      content: (entity) => <JoinKeysCell joinKeys={entity[EntityFCOFields.JOIN_KEYS]} />,
      filterContent: filterComponents[EntitiesListColumnKeys.JOIN_KEYS].filter,
      ...tableArrayLengthSortParameters(sortType, EntityFCOFields.JOIN_KEYS, setSortType),
    },
    {
      name: 'Feature Views',
      id: EntitiesListColumnKeys.FEATURE_VIEWS,
      content: (entity) => {
        const relatedFeatureViews = entity[EntityFCOFields.DEPENDENT_FEATURE_VIEWS].map((id) => idToFcoMap[id]);
        return <RelatedFCOsCell workspace={workspace} fcos={relatedFeatureViews} type={FCOType.FEATURE_VIEW} />;
      },
      filterContent: filterComponents[EntitiesListColumnKeys.FEATURE_VIEWS].filter,
      ...tableArrayLengthSortParameters(sortType, EntityFCOFields.DEPENDENT_FEATURE_VIEWS, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Feature Services',
      id: EntitiesListColumnKeys.FEATURE_SERVICES,
      content: (entity) => {
        const relatedFeatureServices = entity[EntityFCOFields.DEPENDENT_FEATURE_SERVICES].map((id) => idToFcoMap[id]);
        return <RelatedFCOsCell workspace={workspace} fcos={relatedFeatureServices} type={FCOType.FEATURE_SERVICE} />;
      },
      filterContent: filterComponents[EntitiesListColumnKeys.FEATURE_SERVICES].filter,
      ...tableArrayLengthSortParameters(sortType, EntityFCOFields.DEPENDENT_FEATURE_SERVICES, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Last Updated By',
      id: EntitiesListColumnKeys.LAST_UPDATED_BY,
      content: (entity) => (
        <Link avatar={{ name: entity[FCOFields.LAST_MODIFIED_BY] }} name={entity[FCOFields.LAST_MODIFIED_BY]} noHover />
      ),
      filterContent: filterComponents[EntitiesListColumnKeys.LAST_UPDATED_BY].filter,
      columnWidth: 'm',
      ...tableStringSortParameters(sortType, FCOFields.LAST_MODIFIED_BY, setSortType),
    },
    {
      name: 'Last Updated At',
      id: EntitiesListColumnKeys.LAST_UPDATED_AT,
      content: (entity) => <DateCell date={entity[FCOFields.LAST_MODIFIED_DATE]} />,
      filterContent: filterComponents[EntitiesListColumnKeys.LAST_UPDATED_AT].filter,
      ...tableDateSortParameters(sortType, FCOFields.LAST_MODIFIED_DATE, setSortType),
      columnWidth: 'm',
    },
    {
      name: 'Owner',
      id: EntitiesListColumnKeys.CREATED_BY,
      content: (entity) => <Link noHover avatar={{ name: entity[FCOFields.OWNER] }} name={entity[FCOFields.OWNER]} />,
      filterContent: filterComponents[EntitiesListColumnKeys.CREATED_BY].filter,
      ...tableStringSortParameters(sortType, FCOFields.OWNER, setSortType),
    },
    {
      name: 'Created At',
      id: EntitiesListColumnKeys.CREATED_AT,
      content: (entity) => <DateCell date={entity[FCOFields.CREATED_DATE]} />,
      filterContent: filterComponents[EntitiesListColumnKeys.CREATED_AT].filter,
      ...tableDateSortParameters(sortType, FCOFields.CREATED_DATE, setSortType),
      columnWidth: 'm',
    },
  ];

  const selectableColumns = columns.map((column) => {
    return { name: column.name as string, visible: true };
  });

  const [columnState, setColumnState] = useState<{ name: string; visible: boolean }[]>(selectableColumns);

  const columnsSelector = (
    <Select
      groups={[
        {
          items: columnState.map((column) => {
            return {
              name: column.name,
              asToggle: true,
              onClick: () => {
                const newColumnState = columnState.map((selectableColumn) => {
                  if (selectableColumn.name !== column.name) {
                    return selectableColumn;
                  }

                  return {
                    name: selectableColumn.name,
                    visible: !selectableColumn.visible,
                  };
                });
                setColumnState && setColumnState(newColumnState);
              },
              selected: column.visible,
              selectable: true,
            };
          }),
        },
      ]}
      filterable={false}
    />
  );

  const filterEmpty: EmptyProps = {
    actions: <></>,
    placement: 'full-page',
    useCase: 'empty',
    title: 'No Results',
    description: 'No Sources match the current filters. Try adjusting your filters or search query.',
  };

  const filteredColumns = columns
    .map((column) => {
      const matching = columnState.find((stateColumn) => stateColumn.name === column.name);
      if (!matching) {
        return;
      }
      if (matching?.visible !== true) {
        return;
      }

      const asColumn: ExtendedTableColumn<EntityFCO> = {
        ...column,
        isActive: column.id === activeHeading,
        setIsActive: (arg) => {
          if (arg) {
            setActiveHeading(column.id as EntitiesListColumnKeys);
            return;
          }
          setActiveHeading(undefined);
        },
        hideCallback: () => {
          const newColumnState = columnState.map((selectableColumn) => {
            if (selectableColumn.name !== column.name) {
              return selectableColumn;
            }

            return {
              name: selectableColumn.name,
              visible: !selectableColumn.visible,
            };
          });

          setColumnState && setColumnState(newColumnState);
        },
        columnsContent: columnsSelector,
      };

      return asColumn;
    })
    .filter((column) => column !== undefined) as ExtendedTableColumn<EntityFCO>[];

  const activeFilters = Object.values(filterComponents).filter((filter) => {
    return filter.activeCount() !== 0;
  });

  const filteredResults = getFilteredItems(
    items,
    activeFilters,
    [FCOFields.NAME, FCOFields.ID],
    searchQuery ?? '',
    sortType
  );

  return (
    <TableViewLayout
      searchInput={
        <TableSelectors
          hasAnyFilters={hasAnyFilters}
          activeFilterCount={activeFilterCount}
          handleResetFilters={handleResetFilters}
          categories={categories}
          onSearchInputChange={onSearchInputChange}
        />
      }
      filterAndColumnControls={<TableColumnControls columnsSelector={columnsSelector} />}
      table={
        <>
          <Table
            items={filteredResults}
            columns={filteredColumns}
            frozenFirstColumn={true}
            emptyProps={filterEmpty}
            memoized={true}
          />
        </>
      }
    />
  );
};

export default EntitiesList;
